import React, { createContext, useContext, useEffect, useState } from 'react'

const ThemeContext = createContext()

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [systemPreference, setSystemPreference] = useState('light')

  // Check system preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    setSystemPreference(mediaQuery.matches ? 'dark' : 'light')

    const handleChange = (e) => {
      setSystemPreference(e.matches ? 'dark' : 'light')
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Load saved theme preference or use system preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme-preference')
    
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark')
    } else {
      // Default to light mode as requested, but detect system preference for auto mode
      setIsDarkMode(false)
    }
  }, [])

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement
    
    if (isDarkMode) {
      root.setAttribute('data-theme', 'dark')
      root.classList.add('dark-theme')
      root.classList.remove('light-theme')
    } else {
      root.setAttribute('data-theme', 'light')
      root.classList.add('light-theme')
      root.classList.remove('dark-theme')
    }
  }, [isDarkMode])

  const toggleTheme = () => {
    const newTheme = !isDarkMode
    setIsDarkMode(newTheme)
    localStorage.setItem('theme-preference', newTheme ? 'dark' : 'light')
  }

  const setAutoTheme = () => {
    setIsDarkMode(systemPreference === 'dark')
    localStorage.setItem('theme-preference', 'auto')
  }

  const setLightTheme = () => {
    setIsDarkMode(false)
    localStorage.setItem('theme-preference', 'light')
  }

  const setDarkTheme = () => {
    setIsDarkMode(true)
    localStorage.setItem('theme-preference', 'dark')
  }

  const value = {
    isDarkMode,
    systemPreference,
    toggleTheme,
    setAutoTheme,
    setLightTheme,
    setDarkTheme,
    currentTheme: isDarkMode ? 'dark' : 'light'
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}
